import React from 'react'
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router'

import { useStores } from 'hooks/useStores'
import { AuthApi } from 'services/api/AuthApi'
import { AuthStorage } from 'services/storage/AuthStorage'

import { AppButton } from 'components/AppButton'
import { TextField } from 'components/TextField'
import { KG_PHONE_MAX_LENGTH, TextFieldPhone } from 'components/TextFieldPhone'

import { NAV_LIST } from 'constants/nav'

import { ReactComponent as LockIcon } from 'assets/images/svg/lock.svg'

import styles from './AuthPage.module.scss'
import { AuthCodeModal } from './components/AuthCodeModal'

type FormValues = {
  phone: string
  password: string
}
export const AuthPage: React.FC = () => {
  const navigate = useNavigate()
  const { userStore } = useStores()
  const [visible, setVisible] = React.useState(false)
  const [disabled, setDisabled] = React.useState(false)
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'all',
  })

  const watchedPhone = useWatch({
    control,
    name: 'phone',
  })

  const openModal = () => {
    setVisible(true)
  }
  const closeModal = () => {
    setVisible(false)
  }

  const login: SubmitHandler<FormValues> = values => {
    setDisabled(true)
    AuthApi.signIn(values)
      .then(() => {
        openModal()
      })
      .finally(() => {
        setDisabled(false)
      })
  }

  const onPassCode = (code: string) => {
    setDisabled(true)
    AuthApi.assignPassCode({
      phone: watchedPhone,
      code,
    })
      .then(({ data }) => {
        closeModal()
        AuthStorage.setAccessToken(data.token)
        userStore.setUser(data.user)

        const navItem = NAV_LIST.find(item =>
          item.roles.includes(data.user.role),
        )

        if (navItem) {
          if (navItem.reloadDocument) {
            window.location.replace(navItem.path)
            return
          }

          navigate(navItem.path)
        }
      })
      .finally(() => {
        setDisabled(false)
      })
  }

  return (
    <>
      <form onSubmit={handleSubmit(login)} className={styles.wrapper}>
        <div className={styles.container}>
          <div className="title">Вход</div>
          <Controller
            control={control}
            name="phone"
            rules={{
              required: 'Введите телефон',
              min: {
                value: KG_PHONE_MAX_LENGTH,
                message: 'Введите телефон полностью',
              },
            }}
            render={({ field: { value, onChange } }) => {
              return (
                <TextFieldPhone
                  value={value}
                  onChangePhone={event => {
                    onChange(event.formattedPhone)
                  }}
                  withError
                  error={errors.phone}
                />
              )
            }}
          />
          <TextField
            {...register('password', {
              required: 'Введите пароль',
              minLength: {
                value: 8,
                message: 'Минимум 8 символов',
              },
            })}
            type="password"
            label="Пароль"
            icon={<LockIcon />}
            withError
            error={errors.password}
          />
          <div className="pb-16 pt-16">
            <AppButton disabled={disabled} type="submit">
              Войти
            </AppButton>
          </div>
        </div>
      </form>
      {visible ? (
        <AuthCodeModal
          disabled={disabled}
          onClose={closeModal}
          onSave={onPassCode}
        />
      ) : null}
    </>
  )
}
